import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, Checkbox, Chip, IconButton, List, ListItem, Select, Skeleton, StatusChip,
  TextField, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, type components } from '../lib/api';
import { useIstanze } from '../lib/dati';
import { quandoRelativo } from '../lib/attivita';
import { ramoIniziale, trovaRepo, unioneModuli } from '../lib/catalogo';
import { BarraUtente } from '../shell';
import { Back, Copy, Info, Pacchetto, Refresh } from '../icons';
import { Anello, CaricoSezione } from '../brand';
import { ETICHETTA, TONO_ISTANZA } from './instances';
import { t } from '../lib/lingua';
import { usePermessi } from '../session';
import { ConPermesso } from '../permesso';

type RepoCatalogo = components['schemas']['RepoCatalogo'];
type Installazione = components['schemas']['Installazione'];

/**
 * Il catalogo dei moduli: i repository di OCA e delle organizzazioni GitHub
 * dell'azienda, da installare su più istanze in una volta.
 *
 * Non sostituisce «Collega un repository» nella scheda dell'istanza, che resta
 * la strada per GitLab, Bitbucket e i repository fuori catalogo: è il modo di
 * trovarli senza saperne già l'indirizzo, e di non rifare il giro istanza per
 * istanza.
 */

export function useCatalogo(slug: string) {
  return useQuery({
    queryKey: ['catalogo', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/catalog', { params: { path: { slug } } })),
    // L'hub lo ricorda un'ora: qui non serve rileggerlo a ogni ritorno sulla pagina.
    staleTime: 5 * 60_000,
  });
}

export function CatalogoPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/catalogo' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useCatalogo(slug);
  const [cerca, setCerca] = useState('');
  const [fonte, setFonte] = useState('');

  const rileggi = useMutation({
    mutationFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/catalog', { params: { path: { slug }, query: { refresh: true } } })),
    onSuccess: (c) => qc.setQueryData(['catalogo', slug], c),
  });

  const cat = q.data;
  const trovati = useMemo(() => trovaRepo(cat?.repos ?? [], cerca, fonte), [cat, cerca, fonte]);
  const fonti = (cat?.sources ?? []).filter((s) => !s.error);

  return (
    <>
      <TopAppBar
        title={t('Catalogo dei moduli')}
        actions={
          <>
            <Button
              variant="text"
              icon={rileggi.isPending ? <Anello /> : <Refresh />}
              disabled={rileggi.isPending}
              onClick={() => rileggi.mutate()}
            >
              {t('Rileggi')}
            </Button>
            <BarraUtente slug={slug} />
          </>
        }
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('I repository di OCA e delle vostre organizzazioni GitHub. Ne scegli uno, le istanze e il ramo di ciascuna, i moduli: Molo porta il codice e li installa, un’istanza alla volta, con una copia del database prima.')}
          </p>

          {q.error && <Banner tone="error" title={t('Non riesco a leggere il catalogo')}>{q.error.message}</Banner>}
          {rileggi.error && <Banner tone="error" title={t('Non l’ho riletto')}>{(rileggi.error as Error).message}</Banner>}
          {cat && !cat.hasToken && (
            <Banner icon={<Info />} title={t('Solo repository pubblici')}>
              {t('Per vedere anche i vostri privati serve un token GitHub di sola lettura, nelle {impostazioni}. Senza, GitHub concede sessanta letture l’ora.', {
                impostazioni: <Link to="/orgs/$slug/impostazioni" params={{ slug }}>{t('Impostazioni')}</Link>,
              })}
            </Banner>
          )}
          {cat?.tokenRejected && (
            <Banner tone="error" title={t('GitHub non accetta più il token')}>
              {t('È scaduto o è stato revocato: vedi solo i repository pubblici finché non ne metti uno nuovo nelle {impostazioni}.', {
                impostazioni: <Link to="/orgs/$slug/impostazioni" params={{ slug }}>{t('Impostazioni')}</Link>,
              })}
            </Banner>
          )}
          {(cat?.sources ?? []).filter((s) => s.error).map((s) => (
            <Banner key={s.owner} tone="error" title={t('Non leggo {owner}', { owner: s.owner })}>{s.error}</Banner>
          ))}

          {q.isPending && <Skeleton height="12rem" />}

          {cat && (
            <section>
              <div className="md-filtri-server">
                <TextField
                  label=""
                  aria-label={t('Cerca un repository')}
                  placeholder={t('Cerca per nome o descrizione: web, account, l10n-italy…')}
                  value={cerca}
                  onChange={(e) => setCerca(e.target.value)}
                  className="md-filtri-server__cerca"
                />
                {fonti.length > 1 && (
                  <div className="md-filtri-server__chip" role="group" aria-label={t('Fonti')}>
                    {fonti.map((s) => (
                      <Chip key={s.owner} selected={fonte === s.owner} onClick={() => setFonte(fonte === s.owner ? '' : s.owner)}>
                        {s.owner} · {s.repos}
                      </Chip>
                    ))}
                  </div>
                )}
              </div>

              {trovati.length === 0 ? (
                <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                  {(cat.repos ?? []).length === 0
                    ? t('Nessun repository: aggiungi un’organizzazione GitHub nelle Impostazioni.')
                    : cerca.trim()
                      ? t('Nessun repository corrisponde a «{cerca}».', { cerca: cerca.trim() })
                      : t('Nessun repository corrisponde.')}
                </p>
              ) : (
                <List>
                  {trovati.map((r) => {
                    const su = (r.linked ?? []).length;
                    return (
                    <ListItem
                      key={r.url}
                      interactive
                      icon={<Pacchetto />}
                      headline={`${r.owner}/${r.name}`}
                      supporting={[
                        r.description,
                        su === 1 ? t('su 1 istanza') : su > 1 ? t('su {n} istanze', { n: su }) : null,
                        r.pushedAt ? t('aggiornato {quando}', { quando: quandoRelativo(r.pushedAt) }) : null,
                      ].filter(Boolean).join(' · ')}
                      trailing={r.private ? <StatusChip status="stopped" label={t('Privato')} tone="tertiary" /> : undefined}
                      onClick={() =>
                        navigate({ to: '/orgs/$slug/catalogo/$owner/$name', params: { slug, owner: r.owner, name: r.name } })
                      }
                    />
                    );
                  })}
                </List>
              )}
            </section>
          )}
        </div>
      </div>
    </>
  );
}

/** La scelta per un'istanza: se c'è, e con quale ramo. */
type Scelta = { on: boolean; ramo: string; bloccato: boolean };

/**
 * Un repository del catalogo: dove installarlo, con quale ramo, quali moduli.
 *
 * I rami sono quelli veri del repository, letti da GitHub, e ogni istanza
 * parte dalla sua major. I moduli sono quelli dei rami scelti, uniti: un
 * modulo che in un ramo non c'è si dice accanto, e su quell'istanza non
 * arriverà.
 */
export function RepoCatalogoPage() {
  const { slug, owner, name } = useParams({ from: '/shell/orgs/$slug/catalogo/$owner/$name' });
  const permessi = usePermessi(slug);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const avvisa = useSnackbar();

  const cat = useCatalogo(slug);
  const repo: RepoCatalogo | undefined = (cat.data?.repos ?? []).find(
    (r) => r.owner.toLowerCase() === owner.toLowerCase() && r.name.toLowerCase() === name.toLowerCase(),
  );
  const istanze = useIstanze(slug);
  const rami = useQuery({
    queryKey: ['catalogo-rami', slug, owner, name],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/catalog/{owner}/{name}/branches', { params: { path: { slug, owner, name } } })),
  });

  const [scelte, setScelte] = useState<Record<string, Scelta>>({});
  const [moduli, setModuli] = useState<Set<string>>(new Set());
  const [filtro, setFiltro] = useState('');
  const [esito, setEsito] = useState<Installazione | null>(null);

  const elenco = istanze.data?.instances ?? [];
  const ramiVeri = rami.data?.branches ?? [];
  const scelta = (id: string): Scelta | undefined => scelte[id];

  // Il ramo di partenza di ogni istanza, appena si sa quali rami ci sono e
  // dove il repository è già collegato. Non tocca una scelta già fatta: i
  // dati si rileggono, la mano di chi sceglie no. Aspetta anche il catalogo:
  // senza, un'istanza già collegata su un altro ramo partirebbe dalla major.
  useEffect(() => {
    if (!rami.data || !istanze.data || !cat.data) return;
    setScelte((prima) => {
      const dopo = { ...prima };
      for (const i of istanze.data.instances ?? []) {
        if (!dopo[i.id]) dopo[i.id] = { on: false, ...ramoIniziale(i, repo?.linked ?? [], rami.data.branches ?? []) };
      }
      return dopo;
    });
  }, [rami.data, istanze.data, cat.data, repo]);

  const scelte_ = elenco.filter((i) => scelta(i.id)?.on && scelta(i.id)?.ramo);
  const ramiScelti = [...new Set(scelte_.map((i) => scelta(i.id)?.ramo ?? ''))].sort();
  const moduliPerRamo = useQueries({
    queries: ramiScelti.map((ramo) => ({
      queryKey: ['catalogo-moduli', slug, owner, name, ramo],
      queryFn: () =>
        must(api.GET('/api/v1/orgs/{slug}/catalog/{owner}/{name}/modules', {
          params: { path: { slug, owner, name }, query: { branch: ramo } },
        })),
      staleTime: 5 * 60_000,
    })),
  });
  const perRamo: Record<string, string[]> = {};
  moduliPerRamo.forEach((m, i) => {
    const ramo = ramiScelti[i];
    if (m.data && ramo) perRamo[ramo] = m.data.modules ?? [];
  });
  const disponibili = unioneModuli(perRamo);
  const inCarico = moduliPerRamo.some((m) => m.isPending);
  const erroreModuli = moduliPerRamo.find((m) => m.error)?.error;
  const visibili = disponibili.filter((m) => m.nome.includes(filtro.trim().toLowerCase()));
  const scelti = [...moduli].filter((m) => disponibili.some((d) => d.nome === m));

  const installa = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/catalog/install', {
        params: { path: { slug } },
        body: {
          url: repo?.url ?? `https://github.com/${owner}/${name}`,
          instances: scelte_.map((i) => ({ id: i.id, ...(scelta(i.id)?.bloccato ? {} : { branch: scelta(i.id)?.ramo }) })),
          modules: scelti,
        },
      })),
    onSuccess: (r) => {
      setEsito(r);
      // Partito: la scelta si svuota, così un secondo clic non riaccoda. Con
      // la chiave da incollare invece resta, perché «Ho incollato, installa»
      // rifà la stessa richiesta.
      if (!r.publicKey) {
        setModuli(new Set());
        setScelte((s) => Object.fromEntries(Object.entries(s).map(([id, v]) => [id, { ...v, on: false }])));
      }
      qc.invalidateQueries({ queryKey: ['catalogo', slug] });
      qc.invalidateQueries({ queryKey: ['instances', slug] });
      const partiti = (r.results ?? []).filter((e) => e.jobId).length;
      if (partiti > 0) avvisa(partiti === 1 ? t('Partito su un’istanza') : t('Partito su {n} istanze', { n: partiti }));
    },
  });

  const cambia = (id: string, v: Partial<Scelta>) =>
    setScelte((s) => {
      const prima = s[id];
      return prima ? { ...s, [id]: { ...prima, ...v } } : s;
    });
  const pronto = scelte_.length > 0 && !installa.isPending && !inCarico;

  return (
    <>
      <TopAppBar
        leading={
          <IconButton label={t('Torna al catalogo')} onClick={() => navigate({ to: '/orgs/$slug/catalogo', params: { slug } })}>
            <Back />
          </IconButton>
        }
        title={`${owner}/${name}`}
        subtitle={repo?.description}
        actions={<BarraUtente slug={slug} />}
      />
      <CaricoSezione attiva={cat.isPending || rami.isPending || istanze.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {cat.error && <Banner tone="error" title={t('Non riesco a leggere il catalogo')}>{cat.error.message}</Banner>}
          {rami.error && <Banner tone="error" title={t('Non riesco a leggere i rami')}>{rami.error.message}</Banner>}
          <p className="md-body-medium" style={{ margin: 0 }}>
            <a href={repo?.url ?? `https://github.com/${owner}/${name}`} target="_blank" rel="noreferrer">
              {repo?.url ?? `https://github.com/${owner}/${name}`}
            </a>
            {' · '}
            {repo?.private
              ? t('privato: arriva sul server con la chiave di deploy dell’azienda.')
              : t('pubblico: arriva sul server in https, senza chiavi da incollare.')}
          </p>

          <Card variant="outlined">
            <div className="md-section-head">
              <h2 className="md-title-medium">{t('Su quali istanze')}</h2>
              <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                {scelte_.length === 0 ? t('nessuna scelta') : scelte_.length === 1 ? t('1 scelta') : t('{n} scelte', { n: scelte_.length })}
              </span>
            </div>
            {(istanze.isPending || rami.isPending) && <Skeleton height="6rem" />}
            {rami.isSuccess && elenco.length === 0 && (
              <p className="md-body-medium" style={{ margin: 0 }}>{t('Nessuna istanza in questa organizzazione.')}</p>
            )}
            {rami.isSuccess && elenco.length > 0 && (
              <List>
                {elenco.map((i) => {
                  const s = scelta(i.id);
                  const accesa = i.status === 'running';
                  return (
                    <ListItem
                      key={i.id}
                      headline={
                        <Checkbox
                          checked={!!s?.on}
                          disabled={!accesa || !s?.ramo}
                          onCheckedChange={(v) => cambia(i.id, { on: v === true })}
                          label={
                            <span>
                              <b>{i.name}</b>
                              {i.staging ? ` · ${t('copia di prova')}` : ''}
                            </span>
                          }
                        />
                      }
                      supporting={[
                        `Odoo ${i.odooMajor}`,
                        i.serverName,
                        s?.bloccato ? t('già collegato, ramo {ramo}', { ramo: s.ramo }) : null,
                        !accesa ? t('non è accesa') : null,
                        accesa && s && !s.ramo && !s.bloccato ? t('nessun ramo {major}: sceglilo', { major: i.odooMajor }) : null,
                      ].filter(Boolean).join(' · ')}
                      trailing={
                        !accesa ? (
                          <StatusChip status={i.status} label={t(ETICHETTA[i.status] ?? i.status)} tone={TONO_ISTANZA[i.status] ?? 'neutral'} />
                        ) : s && !s.bloccato ? (
                          <Select
                            label={t('Ramo')}
                            value={s.ramo}
                            onChange={(e) => cambia(i.id, { ramo: e.target.value, on: e.target.value !== '' })}
                            options={[
                              { value: '', label: t('Scegli…') },
                              ...ramiVeri.map((r) => ({ value: r, label: r })),
                            ]}
                          />
                        ) : undefined
                      }
                    />
                  );
                })}
              </List>
            )}
          </Card>

          <Card variant="outlined">
            <div className="md-section-head">
              <h2 className="md-title-medium">{t('Quali moduli')}</h2>
              <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                {scelti.length === 0 ? t('nessuno: porto solo il codice') : scelti.length === 1 ? t('1 da installare') : t('{n} da installare', { n: scelti.length })}
              </span>
            </div>
            {erroreModuli && <Banner tone="error" title={t('Non riesco a leggere i moduli')}>{erroreModuli.message}</Banner>}
            {ramiScelti.length === 0 ? (
              <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                {t('Scegli prima le istanze: i moduli dipendono dal ramo.')}
              </p>
            ) : inCarico ? (
              <Skeleton height="6rem" />
            ) : (
              <>
                {disponibili.length > 8 && (
                  <TextField
                    label=""
                    aria-label={t('Cerca un modulo')}
                    placeholder={t('Cerca un modulo…')}
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                  />
                )}
                {disponibili.length === 0 && (
                  <p className="md-body-medium" style={{ margin: 0 }}>
                    {t('In {rami} non ci sono moduli, né nella radice né in una sottocartella.', { rami: ramiScelti.join(', ') })}
                  </p>
                )}
                <div className="md-griglia-moduli">
                  {visibili.map((m) => (
                    <Checkbox
                      key={m.nome}
                      checked={moduli.has(m.nome)}
                      onCheckedChange={(v) =>
                        setModuli((prima) => {
                          const dopo = new Set(prima);
                          if (v === true) dopo.add(m.nome);
                          else dopo.delete(m.nome);
                          return dopo;
                        })
                      }
                      label={
                        <span>
                          <span className="md-mono"><NomeModulo nome={m.nome} /></span>
                          {m.mancaIn.length > 0 && (
                            <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                              {` · ${t('non nel {rami}', { rami: m.mancaIn.join(', ') })}`}
                            </span>
                          )}
                        </span>
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </Card>

          {installa.error && <Banner tone="error" title={t('Non l’ho installato')}>{(installa.error as Error).message}</Banner>}
          {esito && <Esito slug={slug} esito={esito} ripeti={() => installa.mutate()} ripetendo={installa.isPending} />}

          <div className="md-stack">
            <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
              {scelti.length > 0
                ? t('Ogni istanza resta ferma un paio di minuti: prima prendo una copia del database, poi porto il codice e installo. Se l’installazione non riesce, l’istanza si riaccende com’era e il registro del lavoro dice perché. I moduli già installati li salto.')
                : t('Senza moduli porto solo il codice: dopo, da «Moduli…» nella scheda dell’istanza, installi quello che ti serve.')}
            </p>
            <div className="md-stack__inline">
              <ConPermesso ha={permessi} serve={scelti.length > 0 ? ['instance.deploy', 'module.install'] : 'instance.deploy'}>
                <Button
                  variant="filled"
                  icon={installa.isPending ? <Anello /> : undefined}
                  disabled={!pronto}
                  onClick={() => installa.mutate()}
                >
                  {installa.isPending
                    ? t('Avvio…')
                    : scelti.length > 0
                      ? scelte_.length === 1 ? t('Installa su 1 istanza') : t('Installa su {n} istanze', { n: scelte_.length })
                      : scelte_.length === 1 ? t('Porta il codice su 1 istanza') : t('Porta il codice su {n} istanze', { n: scelte_.length })}
                </Button>
              </ConPermesso>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Un nome di modulo che va a capo sugli underscore, dove si legge, invece che
 * a metà di una parola quando la colonna è stretta.
 */
export function NomeModulo({ nome }: { nome: string }) {
  const parti = nome.split('_');
  return (
    <>
      {parti.map((p, i) => (
        <Fragment key={i}>
          {p}
          {i < parti.length - 1 && <>_<wbr /></>}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Com'è andata, istanza per istanza, e per un privato la chiave da incollare
 * se GitHub non la accetta ancora: finché non c'è non si è collegato niente,
 * e «Ho incollato, installa» rifà la stessa richiesta.
 */
function Esito({
  slug, esito, ripeti, ripetendo,
}: { slug: string; esito: Installazione; ripeti: () => void; ripetendo: boolean }) {
  const avvisa = useSnackbar();
  return (
    <Card variant="outlined">
      <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Com’è andata')}</h2>
      {esito.publicKey && (
        <Banner
          icon={<Info />}
          title={t('Incolla questa chiave su GitHub, poi riprova')}
          action={
            <span className="md-stack__inline">
              <Button
                variant="text"
                icon={<Copy />}
                onClick={() => {
                  void navigator.clipboard?.writeText(esito.publicKey ?? '');
                  avvisa(t('Chiave copiata'));
                }}
              >
                {t('Copia')}
              </Button>
              <Button variant="tonal" icon={ripetendo ? <Anello /> : undefined} disabled={ripetendo} onClick={ripeti}>
                {t('Ho incollato, installa')}
              </Button>
            </span>
          }
        >
          <div className="md-stack">
            <p style={{ margin: 0 }}>
              {t('Il repository è privato e la chiave dell’azienda non lo apre ancora{errore}. Su GitHub, nel repository: «Settings → Deploy keys → Add deploy key», {senza} spuntare «Allow write access». È una chiave sola per tutte le istanze. Finché non c’è non ho collegato niente.', {
                // L'errore arriva dall'hub, già nella lingua di chi legge.
                errore: esito.keyError ? ` (${esito.keyError})` : '',
                senza: <b>{t('senza')}</b>,
              })}
            </p>
            <code className="md-mono" style={{ display: 'block', overflowX: 'auto', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
              {esito.publicKey}
            </code>
          </div>
        </Banner>
      )}
      <List>
        {(esito.results ?? []).map((e) => (
          <ListItem
            key={e.instanceId}
            headline={e.instanceName || e.instanceId}
            supporting={[
              e.branch ? t('ramo {ramo}', { ramo: e.branch }) : null,
              (e.install ?? []).length > 0 ? t('installo {moduli}', { moduli: (e.install ?? []).join(', ') }) : e.jobId ? t('porto il codice') : null,
              e.missing?.length ? t('non nel ramo: {moduli}', { moduli: e.missing.join(', ') }) : null,
              e.alreadyInstalled?.length ? t('già installati: {moduli}', { moduli: e.alreadyInstalled.join(', ') }) : null,
              e.error ?? null,
              !e.jobId && !e.error ? t('niente da fare') : null,
            ].filter(Boolean).join(' · ')}
            trailing={
              e.jobId ? (
                <Link to="/orgs/$slug/jobs/$id" params={{ slug, id: e.jobId }}>{t('Segui')}</Link>
              ) : e.error ? (
                <StatusChip status="error" label={t('Non partito')} tone="error" />
              ) : (
                <StatusChip status="ready" label={t('A posto')} tone="success" />
              )
            }
          />
        ))}
      </List>
    </Card>
  );
}
